import { useCallback } from 'react';
import { PenPathInstance } from '../../services/api';
import {
  PenToolSettings,
  LineCap,
  LineJoin,
  ArrowType,
  DashPreset
} from '../../types/penPath';
import Checkbox from '../../components/Checkbox';
import './PenPanel.css';

interface PenPanelProps {
  settings: PenToolSettings;
  onSettingsChange: (settings: PenToolSettings) => void;
  selectedPenPathInstance: PenPathInstance | null;
  selectedCount?: number;
  onUpdatePenPath: (updates: Partial<PenPathInstance>) => void;
  onDeletePenPath: () => void;
  onToggleClosed?: () => void;
  isDrawingPath?: boolean;
}

/**
 * LineCap icon component
 */
function LineCapIcon({ cap, active }: { cap: LineCap; active: boolean }): JSX.Element {
  const color = active ? '#000' : 'currentColor';

  return (
    <svg viewBox="0 0 24 16" className="pen-panel-cap-icon">
      <line x1="2" y1="8" x2="22" y2="8" stroke={color} strokeWidth="4"
        strokeLinecap={cap} />
    </svg>
  );
}

/**
 * LineJoin icon component
 */
function LineJoinIcon({ join, active }: { join: LineJoin; active: boolean }): JSX.Element {
  const color = active ? '#000' : 'currentColor';

  return (
    <svg viewBox="0 0 24 16" className="pen-panel-join-icon">
      <polyline points="2,14 12,2 22,14" fill="none" stroke={color} strokeWidth="3"
        strokeLinejoin={join} strokeLinecap="round" />
    </svg>
  );
}

/**
 * Arrow type icon component
 */
function ArrowIcon({ type, active, isEnd }: { type: ArrowType; active: boolean; isEnd: boolean }): JSX.Element {
  const color = active ? '#000' : 'currentColor';
  const transform = isEnd ? '' : 'scale(-1, 1) translate(-24, 0)';

  const renderArrow = () => {
    switch (type) {
      case 'none':
        return <line x1="4" y1="8" x2="20" y2="8" stroke={color} strokeWidth="2" />;
      case 'arrow':
        return (
          <g transform={transform}>
            <line x1="4" y1="8" x2="18" y2="8" stroke={color} strokeWidth="2" />
            <polyline points="14,4 20,8 14,12" fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
          </g>
        );
      case 'triangle':
        return (
          <g transform={transform}>
            <line x1="4" y1="8" x2="14" y2="8" stroke={color} strokeWidth="2" />
            <polygon points="14,3 22,8 14,13" fill={color} />
          </g>
        );
      case 'circle':
        return (
          <g transform={transform}>
            <line x1="4" y1="8" x2="14" y2="8" stroke={color} strokeWidth="2" />
            <circle cx="18" cy="8" r="4" fill={color} />
          </g>
        );
      case 'square':
        return (
          <g transform={transform}>
            <line x1="4" y1="8" x2="14" y2="8" stroke={color} strokeWidth="2" />
            <rect x="14" y="4" width="8" height="8" fill={color} />
          </g>
        );
      case 'diamond':
        return (
          <g transform={transform}>
            <line x1="4" y1="8" x2="12" y2="8" stroke={color} strokeWidth="2" />
            <polygon points="18,2 24,8 18,14 12,8" fill={color} />
          </g>
        );
      default:
        return null;
    }
  };

  return (
    <svg viewBox="0 0 24 16" className="pen-panel-arrow-preview">
      {renderArrow()}
    </svg>
  );
}

/**
 * Panel for pen tool settings and selected path editing
 */
function PenPanel({
  settings,
  onSettingsChange,
  selectedPenPathInstance,
  selectedCount = 0,
  onUpdatePenPath,
  onDeletePenPath,
  onToggleClosed,
  isDrawingPath = false
}: PenPanelProps): JSX.Element {

  /**
   * Handle setting change (for new path defaults)
   */
  const handleSettingChange = useCallback(<K extends keyof PenToolSettings>(
    key: K,
    value: PenToolSettings[K]
  ) => {
    onSettingsChange({ ...settings, [key]: value });
  }, [settings, onSettingsChange]);

  /**
   * Handle selected path update
   */
  const handlePathUpdate = useCallback(<K extends keyof PenPathInstance>(
    key: K,
    value: PenPathInstance[K]
  ) => {
    onUpdatePenPath({ [key]: value });
  }, [onUpdatePenPath]);

  /**
   * Render stroke controls
   */
  const renderStrokeControls = (
    currentStrokeColor: string | null,
    currentStrokeWidth: number,
    currentStrokeOpacity: number,
    currentLineCap: LineCap,
    currentLineJoin: LineJoin,
    currentDashPreset: DashPreset,
    isSelected: boolean,
    currentFillColor: string | null
  ) => (
    <>
      {/* Stroke Enable/Color */}
      <div className="pen-panel-control">
        <label className="pen-panel-label">Stroke</label>
        <div className="pen-panel-color-row">
          <Checkbox
            label=""
            checked={currentStrokeColor !== null}
            onChange={(checked) => {
              // Prevent disabling both stroke and fill
              if (!checked && currentFillColor === null) {
                // If trying to disable stroke but fill is also disabled, enable fill first
                if (isSelected) {
                  handlePathUpdate('fillColor', '#3388ff');
                } else {
                  handleSettingChange('fillColor', '#3388ff');
                }
              }
              const val = checked ? '#000000' : null;
              isSelected
                ? handlePathUpdate('strokeColor', val)
                : handleSettingChange('strokeColor', val as string);
            }}
          />
          {currentStrokeColor !== null && (
            <>
              <input
                type="color"
                className="pen-panel-color"
                value={currentStrokeColor}
                onChange={(e) => isSelected
                  ? handlePathUpdate('strokeColor', e.target.value)
                  : handleSettingChange('strokeColor', e.target.value)
                }
              />
              <input
                type="text"
                className="pen-panel-color-input"
                value={currentStrokeColor}
                onChange={(e) => isSelected
                  ? handlePathUpdate('strokeColor', e.target.value)
                  : handleSettingChange('strokeColor', e.target.value)
                }
              />
            </>
          )}
        </div>
      </div>

      {currentStrokeColor !== null && (
        <>
          {/* Stroke Width */}
          <div className="pen-panel-control">
            <label className="pen-panel-label">Width</label>
            <input
              type="number"
              className="pen-panel-width-input"
              value={currentStrokeWidth}
              onChange={(e) => {
                const val = Math.max(0.5, Math.min(50, parseFloat(e.target.value) || 1));
                isSelected
                  ? handlePathUpdate('strokeWidth', val)
                  : handleSettingChange('strokeWidth', val);
              }}
              min={0.5}
              max={50}
              step={0.5}
            />
            <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>px</span>
          </div>

          {/* Stroke Opacity */}
          <div className="pen-panel-control">
            <label className="pen-panel-label">Opacity</label>
            <div className="pen-panel-opacity-row">
              <input
                type="range"
                className="pen-panel-opacity-slider"
                min={0}
                max={1}
                step={0.01}
                value={currentStrokeOpacity}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  isSelected
                    ? handlePathUpdate('strokeOpacity', val)
                    : handleSettingChange('strokeOpacity', val);
                }}
              />
              <span className="pen-panel-opacity-value">
                {Math.round(currentStrokeOpacity * 100)}%
              </span>
            </div>
          </div>

          {/* Dash Pattern */}
          <div className="pen-panel-control">
            <label className="pen-panel-label">Style</label>
            <div className="pen-panel-button-group">
              {(['solid', 'dashed', 'dotted'] as DashPreset[]).map((preset) => (
                <button
                  key={preset}
                  className={`pen-panel-btn ${currentDashPreset === preset ? 'pen-panel-btn--active' : ''}`}
                  onClick={() => isSelected
                    ? handlePathUpdate('dashPreset', preset)
                    : handleSettingChange('dashPreset', preset)
                  }
                  title={preset.charAt(0).toUpperCase() + preset.slice(1)}
                >
                  {preset === 'solid' && <span style={{ letterSpacing: 0 }}>---</span>}
                  {preset === 'dashed' && <span style={{ letterSpacing: '2px' }}>- -</span>}
                  {preset === 'dotted' && <span style={{ letterSpacing: '3px' }}>...</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Line Cap */}
          <div className="pen-panel-control">
            <label className="pen-panel-label">Cap</label>
            <div className="pen-panel-button-group">
              {(['butt', 'round', 'square'] as LineCap[]).map((cap) => (
                <button
                  key={cap}
                  className={`pen-panel-btn pen-panel-btn--icon-only ${currentLineCap === cap ? 'pen-panel-btn--active' : ''}`}
                  onClick={() => isSelected
                    ? handlePathUpdate('lineCap', cap)
                    : handleSettingChange('lineCap', cap)
                  }
                  title={cap.charAt(0).toUpperCase() + cap.slice(1)}
                >
                  <LineCapIcon cap={cap} active={currentLineCap === cap} />
                </button>
              ))}
            </div>
          </div>

          {/* Line Join */}
          <div className="pen-panel-control">
            <label className="pen-panel-label">Join</label>
            <div className="pen-panel-button-group">
              {(['miter', 'round', 'bevel'] as LineJoin[]).map((join) => (
                <button
                  key={join}
                  className={`pen-panel-btn pen-panel-btn--icon-only ${currentLineJoin === join ? 'pen-panel-btn--active' : ''}`}
                  onClick={() => isSelected
                    ? handlePathUpdate('lineJoin', join)
                    : handleSettingChange('lineJoin', join)
                  }
                  title={join.charAt(0).toUpperCase() + join.slice(1)}
                >
                  <LineJoinIcon join={join} active={currentLineJoin === join} />
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );

  /**
   * Render arrow controls
   */
  const renderArrowControls = (
    currentArrowStart: ArrowType,
    currentArrowEnd: ArrowType,
    isSelected: boolean
  ) => (
    <>
      {/* Arrow Start */}
      <div className="pen-panel-control">
        <label className="pen-panel-label">Start</label>
        <select
          className="pen-panel-select"
          value={currentArrowStart}
          onChange={(e) => isSelected
            ? handlePathUpdate('arrowStart', e.target.value as ArrowType)
            : handleSettingChange('arrowStart', e.target.value as ArrowType)
          }
        >
          <option value="none">None</option>
          <option value="arrow">Arrow</option>
          <option value="triangle">Triangle</option>
          <option value="circle">Circle</option>
          <option value="square">Square</option>
          <option value="diamond">Diamond</option>
        </select>
        <ArrowIcon type={currentArrowStart} active={false} isEnd={false} />
      </div>

      {/* Arrow End */}
      <div className="pen-panel-control">
        <label className="pen-panel-label">End</label>
        <select
          className="pen-panel-select"
          value={currentArrowEnd}
          onChange={(e) => isSelected
            ? handlePathUpdate('arrowEnd', e.target.value as ArrowType)
            : handleSettingChange('arrowEnd', e.target.value as ArrowType)
          }
        >
          <option value="none">None</option>
          <option value="arrow">Arrow</option>
          <option value="triangle">Triangle</option>
          <option value="circle">Circle</option>
          <option value="square">Square</option>
          <option value="diamond">Diamond</option>
        </select>
        <ArrowIcon type={currentArrowEnd} active={false} isEnd={true} />
      </div>
    </>
  );

  /**
   * Render fill controls
   */
  const renderFillControls = (
    currentFillColor: string | null,
    currentFillOpacity: number,
    currentFeatherRadius: number,
    currentInvertFill: boolean,
    isSelected: boolean,
    currentStrokeColor: string | null
  ) => (
    <>
      {/* Fill Enable/Color */}
      <div className="pen-panel-control">
        <label className="pen-panel-label">Fill</label>
        <div className="pen-panel-color-row">
          <Checkbox
            label=""
            checked={currentFillColor !== null}
            onChange={(checked) => {
              // Prevent disabling both stroke and fill
              if (!checked && currentStrokeColor === null) {
                // If trying to disable fill but stroke is also disabled, enable stroke first
                if (isSelected) {
                  handlePathUpdate('strokeColor', '#000000');
                } else {
                  handleSettingChange('strokeColor', '#000000');
                }
              }
              const val = checked ? '#3388ff' : null;
              isSelected
                ? handlePathUpdate('fillColor', val)
                : handleSettingChange('fillColor', val);
            }}
          />
          {currentFillColor !== null && (
            <>
              <input
                type="color"
                className="pen-panel-color"
                value={currentFillColor}
                onChange={(e) => isSelected
                  ? handlePathUpdate('fillColor', e.target.value)
                  : handleSettingChange('fillColor', e.target.value)
                }
              />
              <input
                type="text"
                className="pen-panel-color-input"
                value={currentFillColor}
                onChange={(e) => isSelected
                  ? handlePathUpdate('fillColor', e.target.value)
                  : handleSettingChange('fillColor', e.target.value)
                }
              />
            </>
          )}
        </div>
      </div>

      {currentFillColor !== null && (
        <>
          <div className="pen-panel-control">
            <label className="pen-panel-label">Opacity</label>
            <div className="pen-panel-opacity-row">
              <input
                type="range"
                className="pen-panel-opacity-slider"
                min={0}
                max={1}
                step={0.01}
                value={currentFillOpacity}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  isSelected
                    ? handlePathUpdate('fillOpacity', val)
                    : handleSettingChange('fillOpacity', val);
                }}
              />
              <span className="pen-panel-opacity-value">
                {Math.round(currentFillOpacity * 100)}%
              </span>
            </div>
          </div>

          <div className="pen-panel-control">
            <label className="pen-panel-label">Feather</label>
            <div className="pen-panel-opacity-row">
              <input
                type="range"
                className="pen-panel-opacity-slider"
                min={0}
                max={500}
                step={1}
                value={currentFeatherRadius}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  isSelected
                    ? handlePathUpdate('featherRadius', val)
                    : handleSettingChange('featherRadius', val);
                }}
              />
              <span className="pen-panel-opacity-value">
                {Math.round(currentFeatherRadius)}px
              </span>
            </div>
          </div>

          <div className="pen-panel-control">
            <label className="pen-panel-label">Invert</label>
            <div className="pen-panel-color-row">
              <Checkbox
                label="Cutout mode"
                checked={currentInvertFill}
                onChange={(checked) => {
                  isSelected
                    ? handlePathUpdate('invertFill', checked)
                    : handleSettingChange('invertFill', checked);
                }}
              />
            </div>
          </div>
        </>
      )}
    </>
  );

  return (
    <div className="pen-panel">
      {/* Selected Path Instance Controls */}
      {selectedPenPathInstance && (
        <div className="pen-panel-section pen-panel-selected">
          <div className="pen-panel-section-title">
            {selectedCount > 1 ? `${selectedCount} Paths Selected` : 'Selected Path'}
            <button
              className="pen-panel-deselect"
              onClick={() => onUpdatePenPath({})}
              title="Deselect"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Stroke controls for selected path */}
          <div className="pen-panel-controls">
            {renderStrokeControls(
              selectedPenPathInstance.strokeColor,
              selectedPenPathInstance.strokeWidth,
              selectedPenPathInstance.strokeOpacity,
              selectedPenPathInstance.lineCap as LineCap,
              selectedPenPathInstance.lineJoin as LineJoin,
              selectedPenPathInstance.dashPreset as DashPreset,
              true,
              selectedPenPathInstance.fillColor
            )}
          </div>

          <div className="pen-panel-divider" />

          {/* Arrow controls for selected path */}
          <div className="pen-panel-controls">
            {renderArrowControls(
              selectedPenPathInstance.arrowStart as ArrowType,
              selectedPenPathInstance.arrowEnd as ArrowType,
              true
            )}
          </div>

          <div className="pen-panel-divider" />

          {/* Fill controls for selected path */}
          <div className="pen-panel-controls">
            {renderFillControls(
              selectedPenPathInstance.fillColor,
              selectedPenPathInstance.fillOpacity,
              selectedPenPathInstance.featherRadius ?? 0,
              selectedPenPathInstance.invertFill ?? false,
              true,
              selectedPenPathInstance.strokeColor
            )}
          </div>

          {/* Actions */}
          <div className="pen-panel-actions">
            {selectedCount === 1 && onToggleClosed && (
              <button
                className="pen-panel-action-btn"
                onClick={onToggleClosed}
                title={selectedPenPathInstance.closed ? 'Open Path' : 'Close Path'}
              >
                <i className={`fas ${selectedPenPathInstance.closed ? 'fa-unlock' : 'fa-lock'}`}></i>
                <span>{selectedPenPathInstance.closed ? 'Open' : 'Close'}</span>
              </button>
            )}
            <button
              className="pen-panel-action-btn pen-panel-action-btn--danger"
              onClick={onDeletePenPath}
              title={selectedCount > 1 ? `Delete ${selectedCount} Paths` : 'Delete Path'}
            >
              <i className="fas fa-trash"></i>
              <span>Delete</span>
            </button>
          </div>
        </div>
      )}

      {/* New Path Settings */}
      {!selectedPenPathInstance && (
        <>
          <div className="pen-panel-section">
            <div className="pen-panel-section-title">
              <span>Stroke</span>
            </div>
            <div className="pen-panel-controls">
              {renderStrokeControls(
                settings.strokeColor,
                settings.strokeWidth,
                settings.strokeOpacity,
                settings.lineCap,
                settings.lineJoin,
                settings.dashPreset,
                false,
                settings.fillColor
              )}
            </div>
          </div>

          <div className="pen-panel-section">
            <div className="pen-panel-section-title">
              <span>Arrows</span>
            </div>
            <div className="pen-panel-controls">
              {renderArrowControls(
                settings.arrowStart,
                settings.arrowEnd,
                false
              )}
            </div>
          </div>

          <div className="pen-panel-section">
            <div className="pen-panel-section-title">
              <span>Fill</span>
            </div>
            <div className="pen-panel-controls">
              {renderFillControls(
                settings.fillColor,
                settings.fillOpacity,
                settings.featherRadius,
                settings.invertFill,
                false,
                settings.strokeColor
              )}
            </div>
          </div>
        </>
      )}

      {/* Help Text */}
      <div className="pen-panel-help">
        <div className="pen-panel-help-item">
          <kbd>Click</kbd> Place point / Select
        </div>
        <div className="pen-panel-help-item">
          <kbd>Click + Drag</kbd> Create curve handles
        </div>
        <div className="pen-panel-help-item">
          <kbd>Alt + Drag</kbd> Break handles (corner)
        </div>
        <div className="pen-panel-help-item">
          <kbd>Double-click</kbd> / <kbd>Enter</kbd> End open path
        </div>
        <div className="pen-panel-help-item">
          <kbd>Click start</kbd> Close path
        </div>
        <div className="pen-panel-help-item">
          <kbd>Ctrl + C</kbd> Copy selected
        </div>
        <div className="pen-panel-help-item">
          <kbd>Ctrl + X</kbd> Cut selected
        </div>
        <div className="pen-panel-help-item">
          <kbd>Ctrl + V</kbd> Paste at cursor
        </div>
        <div className="pen-panel-help-item">
          <kbd>Del</kbd> Delete selected
        </div>
        <div className="pen-panel-help-item">
          <kbd>Esc</kbd> Cancel / Deselect
        </div>
      </div>
    </div>
  );
}

export default PenPanel;
